/**
 * ====================================================================
 * AP.js - 汎用スクロールインジケーター＆UIコンポーネント集
 * ====================================================================
 * 
 * 【主な機能】
 * １. AP汎用スクロールインジケーター（推奨）
 * ２. マウスドラッグによる横スクロール機能
 * ３. 商品詳細ページ用アコーディオン
 * 
 * 【基本的な使用方法】
 * ■ AP汎用スクロールインジケーター（推奨）
 * HTML構造:
 * <div class="wrapper">
 *   <div class="ap-scroll-container" data-ap-scroll="container">
 *     <div class="item">アイテム1</div>
 *     <div class="item">アイテム2</div>
 *   </div>
 *   <div class="ap-scroll-indicator" data-ap-scroll="indicator">
 *     <div class="ap-scroll-indicator__thumb"></div>
 *   </div>
 * </div>
 * 
 * オプション属性:
 * - data-items="数値": アイテム総数
 * - data-no-scroll-desktop="数値": デスクトップで非表示にするアイテム数
 * - data-no-scroll-tablet="数値": タブレットで非表示にするアイテム数  
 * - data-no-scroll-mobile="数値": モバイルで非表示にするアイテム数
 * 
 * ■ マウスドラッグ横スクロール
 * data-ap-scroll="container"を持つ要素に自動適用
 * 
 * ■ アコーディオン
 * HTML構造:
 * <div class="custom-accordion-item">
 *   <div class="custom-title">タイトル</div>
 *   <div class="custom-content">コンテンツ</div>
 * </div>
 */

/* ────────────────────────────────────────────────────────────── */
/* ===== 1. AP汎用スクロールインジケーター（推奨） ===== */
/* ────────────────────────────────────────────────────────────── */
/**
 * フル機能版スクロールインジケーター
 * 
 * 【機能】
 * - インジケーターバーのドラッグ操作
 * - コンテナ内でのマウスドラッグスクロール
 * - レスポンシブ対応（自動表示/非表示切り替え）
 * - 画像読み込み完了監視
 * - リサイズ対応
 * 
 * 【使用条件】
 * - data-ap-scroll="container" のみ（data-items属性なし）
 * - より高機能だが、レスポンシブ制御は手動実装が必要
 */

class APScrollIndicator {
  constructor(container, indicator) {
    this.container = container;
    this.indicator = indicator;
    this.thumb = indicator.querySelector('.ap-scroll-indicator__thumb');
    this.isDragging = false;
    this.isContainerDragging = false;
    this.startX = 0;
    this.startScrollLeft = 0;
    this.containerStartX = 0;
    this.containerStartScrollLeft = 0;
    this.imagesLoaded = false;
    this.checkImagesLoadedTimeout = null;

    this.init();
  }

  init() {
    this.updateIndicator();
    this.checkScrollable();
    this.bindEvents();
    this.setupImageLoadWatcher();
    this.setupResizeObserver();
  }

  updateIndicator() {
    const scrollLeft = this.container.scrollLeft;
    
    // paddingを考慮したスクロール幅計算
    const computedStyle = window.getComputedStyle(this.container);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    
    // 実際のコンテンツ幅（paddingを除いた部分）
    const contentWidth = this.container.clientWidth - paddingLeft - paddingRight;
    // スクロール可能な範囲（paddingを考慮）
    const scrollWidth = this.container.scrollWidth - this.container.clientWidth;
    
    if (scrollWidth <= 0) {
      this.indicator.classList.remove('visible');
      return;
    }
    
    this.indicator.classList.add('visible');
    
    // サムの幅をコンテンツ幅に基づいて計算
    const thumbWidth = (contentWidth / (this.container.scrollWidth - paddingLeft - paddingRight)) * 100;
    const scrollRatio = scrollLeft / scrollWidth;
    const maxThumbPosition = 100 - thumbWidth;
    const thumbPosition = scrollRatio * maxThumbPosition;
    
    this.thumb.style.width = thumbWidth + '%';
    this.thumb.style.left = thumbPosition + '%';
    this.thumb.style.transform = 'none';
  }

  checkScrollable() {
    // paddingを考慮してスクロール可能かどうかをチェック
    const computedStyle = window.getComputedStyle(this.container);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    
    // より正確な判定のため、実際のスクロール可能幅を確認
    const actualScrollWidth = this.container.scrollWidth;
    const actualClientWidth = this.container.clientWidth;
    // 微細な差（2px以下）は無視してスクロール判定
    let scrollable = actualScrollWidth > actualClientWidth + 2;

    // 汎用的なスクロール無効化判定
    const totalItems = parseInt(this.container.getAttribute('data-items') || '0');
    const noScrollDesktop = parseInt(this.container.getAttribute('data-no-scroll-desktop') || '0');
    const noScrollTablet = parseInt(this.container.getAttribute('data-no-scroll-tablet') || '0');
    const noScrollMobile = parseInt(this.container.getAttribute('data-no-scroll-mobile') || '0');
    
    if (totalItems > 0) {
      const screenWidth = window.innerWidth;
      
      // ブレークポイント別の無効化判定
      if (screenWidth >= 1000 && noScrollDesktop > 0 && totalItems <= noScrollDesktop) {
        scrollable = false;
      } else if (screenWidth >= 700 && screenWidth < 1000 && noScrollTablet > 0 && totalItems <= noScrollTablet) {
        scrollable = false;
      } else if (screenWidth < 700 && noScrollMobile > 0 && totalItems <= noScrollMobile) {
        scrollable = false;
      }
    }

    if (scrollable) {
      this.indicator.classList.add('visible');
      // スクロール有効時はno-scrollクラスを削除
      this.container.classList.remove('ap-no-scroll');
    } else {
      this.indicator.classList.remove('visible');
      // スクロール無効時はno-scrollクラスを追加（センタリング表示）
      this.container.classList.add('ap-no-scroll');
    }
    this.updateIndicator();
  }

  getEventX(e) {
    return e.touches ? e.touches[0].clientX : e.clientX;
  }

  bindEvents() {
    // スクロールイベント
    this.container.addEventListener('scroll', () => {
      if (!this.isDragging && !this.isContainerDragging) {
        this.updateIndicator();
      }
    });

    // コンテナのマウスドラッグイベント
    this.container.addEventListener('mousedown', (e) => {
      // リンクやボタンがクリックされた場合はドラッグを無効にする
      if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON' || e.target.closest('a') || e.target.closest('button')) {
        return;
      }
      this.handleContainerMouseDown(e);
    });

    // インジケーターのマウスダウンイベント
    this.indicator.addEventListener('mousedown', (e) => {
      this.handleMouseDown(e);
    });

    // タッチイベント
    this.indicator.addEventListener('touchstart', (e) => {
      this.handleMouseDown(e);
    }, { passive: false });

    this.container.addEventListener('touchstart', (e) => {
      if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON' || e.target.closest('a') || e.target.closest('button')) {
        return;
      }
      this.handleContainerMouseDown(e);
    }, { passive: false });

    // グローバルマウスイベント
    document.addEventListener('mousemove', (e) => {
      if (this.isDragging) {
        this.handleMouseMove(e);
      } else if (this.isContainerDragging) {
        this.handleContainerMouseMove(e);
      }
    });

    document.addEventListener('touchmove', (e) => {
      if (this.isDragging) {
        this.handleMouseMove(e);
      } else if (this.isContainerDragging) {
        this.handleContainerMouseMove(e);
      }
    }, { passive: false });

    document.addEventListener('mouseup', () => {
      this.handleMouseUp();
      this.handleContainerMouseUp();
    });

    document.addEventListener('touchend', () => {
      this.handleMouseUp();
      this.handleContainerMouseUp();
    });

    // リサイズイベント
    window.addEventListener('resize', () => {
      this.checkScrollable();
    });

    // コンテナのクリックイベントを阻止（ドラッグ後のクリックを防ぐ）
    this.container.addEventListener('click', (e) => {
      if (this.containerHasDragged) {
        e.preventDefault();
        e.stopPropagation();
        this.containerHasDragged = false;
      }
    }, true);
  }

  // コンテナドラッグ関連のメソッド
  handleContainerMouseDown(e) {
    this.isContainerDragging = true;
    this.containerStartX = this.getEventX(e);
    this.containerStartScrollLeft = this.container.scrollLeft;
    this.containerHasDragged = false;
    
    this.container.style.cursor = 'grabbing';
    this.container.style.userSelect = 'none';
    
    if (e.preventDefault) {
      e.preventDefault();
    }
  }

  handleContainerMouseMove(e) {
    if (!this.isContainerDragging) return;
    
    if (e.preventDefault) {
      e.preventDefault();
    }
    
    const deltaX = this.containerStartX - this.getEventX(e);
    const targetScrollLeft = this.containerStartScrollLeft + deltaX;
    const scrollWidth = this.container.scrollWidth - this.container.clientWidth;
    
    // ドラッグしたと判定する閾値
    if (Math.abs(deltaX) > 5) {
      this.containerHasDragged = true;
    }
    
    this.container.scrollLeft = Math.max(0, Math.min(targetScrollLeft, scrollWidth));
    this.updateIndicator();
  }

  handleContainerMouseUp() {
    if (!this.isContainerDragging) return;
    
    this.isContainerDragging = false;
    this.container.style.cursor = '';
    this.container.style.userSelect = '';
  }

  // インジケーター関連のメソッド（既存）
  handleMouseDown(e) {
    this.isDragging = true;
    this.startX = this.getEventX(e);
    this.startScrollLeft = this.container.scrollLeft;
    
    this.thumb.classList.add('is-dragging');
    this.container.classList.add('is-dragging');
    
    const rect = this.indicator.getBoundingClientRect();
    const clickPosition = this.getEventX(e) - rect.left;
    const indicatorWidth = rect.width;
    const scrollWidth = this.container.scrollWidth - this.container.clientWidth;
    
    const clickRatio = Math.max(0, Math.min(1, clickPosition / indicatorWidth));
    const targetScrollLeft = clickRatio * scrollWidth;
    
    this.container.scrollLeft = targetScrollLeft;
    this.startScrollLeft = targetScrollLeft;
    
    if (e.preventDefault) {
      e.preventDefault();
    }
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    
    if (e.preventDefault) {
      e.preventDefault();
    }
    
    const rect = this.indicator.getBoundingClientRect();
    const indicatorWidth = rect.width;
    const scrollWidth = this.container.scrollWidth - this.container.clientWidth;
    
    const deltaX = this.getEventX(e) - this.startX;
    const deltaScrollRatio = deltaX / indicatorWidth;
    const targetScrollLeft = this.startScrollLeft + (deltaScrollRatio * scrollWidth);
    
    const clampedScrollLeft = Math.max(0, Math.min(targetScrollLeft, scrollWidth));
    this.container.scrollLeft = clampedScrollLeft;
    
    this.updateIndicatorPosition(clampedScrollLeft, scrollWidth);
  }

  updateIndicatorPosition(scrollLeft, scrollWidth) {
    // paddingを考慮したサム幅計算
    const computedStyle = window.getComputedStyle(this.container);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const contentWidth = this.container.clientWidth - paddingLeft - paddingRight;
    
    const thumbWidth = (contentWidth / (this.container.scrollWidth - paddingLeft - paddingRight)) * 100;
    const scrollRatio = scrollLeft / scrollWidth;
    const maxThumbPosition = 100 - thumbWidth;
    const thumbPosition = scrollRatio * maxThumbPosition;
    
    this.thumb.style.left = thumbPosition + '%';
  }

  handleMouseUp() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.thumb.classList.remove('is-dragging');
    this.container.classList.remove('is-dragging');
    
    setTimeout(() => {
      this.updateIndicator();
    }, 50);
  }

  // 画像読み込み監視
  setupImageLoadWatcher() {
    const images = this.container.querySelectorAll('img');
    let loadedCount = 0;
    let totalImages = images.length;

    if (totalImages === 0) {
      this.imagesLoaded = true;
      this.recheckIndicator();
      return;
    }

    const checkComplete = () => {
      loadedCount++;
      if (loadedCount >= totalImages) {
        this.imagesLoaded = true;
        this.recheckIndicator();
      }
    };

    images.forEach(img => {
      if (img.complete && img.naturalHeight !== 0) {
        checkComplete();
      } else {
        img.addEventListener('load', checkComplete, { once: true });
        img.addEventListener('error', checkComplete, { once: true });
      }
    });

    // タイムアウト保護（3秒後に強制的に完了とみなす）
    this.checkImagesLoadedTimeout = setTimeout(() => {
      if (!this.imagesLoaded) {
        this.imagesLoaded = true;
        this.recheckIndicator();
      }
    }, 3000);
  }

  // ResizeObserverでコンテナサイズ変更を監視
  setupResizeObserver() {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.recheckIndicator();
      });
      this.resizeObserver.observe(this.container);
    }
  }

  // インジケーター再チェック
  recheckIndicator() {
    // 少し遅延させて DOM の更新を待つ
    setTimeout(() => {
      this.checkScrollable();
      this.updateIndicator();
    }, 100);
  }

  // クリーンアップ
  destroy() {
    if (this.checkImagesLoadedTimeout) {
      clearTimeout(this.checkImagesLoadedTimeout);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

/* ────────────────────────────────────────────────────────────── */
/* ===== 2. Data属性対応スクロールインジケーター（簡易版） ===== */
/* ────────────────────────────────────────────────────────────── */
/**
 * データ属性による自動レスポンシブ制御対応版
 * 
 * 【機能】
 * - インジケーターバーのドラッグ操作（NEW）
 * - データ属性による自動表示/非表示制御
 * - ブレークポイント別の細かい制御
 * 
 * 【使用条件】
 * - data-ap-scroll="container" + data-items="数値" 
 * - レスポンシブ制御が自動化されている
 * - 設定が簡単で初心者向け
 */

function setupDataScrollIndicator(container, indicator) {
  const thumb = indicator.querySelector('.ap-scroll-indicator__thumb');
  
  function updateIndicator() {
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth - container.clientWidth;
    
    if (scrollWidth <= 0) {
      indicator.classList.remove('visible');
      return;
    }
    
    indicator.classList.add('visible');
    
    // シンプルで確実な標準計算
    const thumbWidth = (container.clientWidth / container.scrollWidth) * 100;
    const scrollRatio = scrollLeft / scrollWidth;
    const maxThumbPosition = 100 - thumbWidth;
    const thumbPosition = scrollRatio * maxThumbPosition;
    
    thumb.style.width = Math.max(10, thumbWidth) + '%';
    thumb.style.left = Math.min(maxThumbPosition, thumbPosition) + '%';
  }
  
  function checkScrollable() {
    const totalItems = parseInt(container.getAttribute('data-items') || '0');
    const noScrollDesktop = parseInt(container.getAttribute('data-no-scroll-desktop') || '0');
    const noScrollTablet = parseInt(container.getAttribute('data-no-scroll-tablet') || '0');
    const noScrollMobile = parseInt(container.getAttribute('data-no-scroll-mobile') || '0');
    
    // gap分を考慮した許容範囲でスクロール判定
    const computedStyle = window.getComputedStyle(container);
    const gap = parseFloat(computedStyle.gap) || 0;
    const tolerance = Math.max(10, gap + 2); // gap + 最小許容値
    let scrollable = container.scrollWidth > container.clientWidth + tolerance;
    
    if (totalItems > 0) {
      const screenWidth = window.innerWidth;
      
      if (screenWidth >= 1000 && noScrollDesktop > 0 && totalItems <= noScrollDesktop) {
        scrollable = false;
      } else if (screenWidth >= 700 && screenWidth < 1000 && noScrollTablet > 0 && totalItems <= noScrollTablet) {
        scrollable = false;
      } else if (screenWidth < 700 && noScrollMobile > 0 && totalItems <= noScrollMobile) {
        scrollable = false;
      }
    }
    
    if (scrollable) {
      indicator.classList.add('visible');
      container.classList.remove('ap-no-scroll');
    } else {
      indicator.classList.remove('visible');
      container.classList.add('ap-no-scroll');
    }
    updateIndicator();
  }
  
  // ドラッグ機能（最適化版）
  let isDragging = false;
  let startX = 0;
  let startScrollLeft = 0;
  let rafId = null;
  let lastMoveEvent = null;
  
  function handleMouseDown(e) {
    isDragging = true;
    startX = e.clientX || e.touches[0].clientX;
    startScrollLeft = container.scrollLeft;
    thumb.classList.add('is-dragging');
    container.classList.add('is-dragging');
    
    // ドラッグ中はscroll-snapを無効化
    container.style.scrollSnapType = 'none';
    container.style.scrollBehavior = 'auto';
    
    if (e.preventDefault) {
      e.preventDefault();
    }
  }
  
  function handleMouseMove(e) {
    if (!isDragging) return;
    
    lastMoveEvent = e;
    
    // requestAnimationFrameで処理を最適化
    if (!rafId) {
      rafId = requestAnimationFrame(updateDragPosition);
    }
  }
  
  function updateDragPosition() {
    rafId = null;
    
    if (!lastMoveEvent || !isDragging) return;
    
    const currentX = lastMoveEvent.clientX || lastMoveEvent.touches[0].clientX;
    const rect = indicator.getBoundingClientRect();
    const indicatorWidth = rect.width;
    const scrollWidth = container.scrollWidth - container.clientWidth;
    
    const deltaX = currentX - startX;
    const deltaScrollRatio = deltaX / indicatorWidth;
    const targetScrollLeft = startScrollLeft + (deltaScrollRatio * scrollWidth);
    
    const clampedScrollLeft = Math.max(0, Math.min(targetScrollLeft, scrollWidth));
    container.scrollLeft = clampedScrollLeft;
    
    // インジケーター位置を直接更新（updateIndicator()より軽量）
    const scrollRatio = clampedScrollLeft / scrollWidth;
    const thumbWidth = (container.clientWidth / container.scrollWidth) * 100;
    const maxThumbPosition = 100 - thumbWidth;
    const thumbPosition = scrollRatio * maxThumbPosition;
    
    thumb.style.left = Math.min(maxThumbPosition, thumbPosition) + '%';
  }
  
  function handleMouseUp() {
    if (!isDragging) return;
    
    isDragging = false;
    thumb.classList.remove('is-dragging');
    container.classList.remove('is-dragging');
    
    // scroll-snapを復旧
    container.style.scrollSnapType = '';
    container.style.scrollBehavior = '';
    
    // 最後にインジケーターを正確な位置に調整
    setTimeout(() => {
      updateIndicator();
    }, 50);
    
    // 未処理のrAFをクリア
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }
  
  // イベント設定
  indicator.addEventListener('mousedown', handleMouseDown);
  indicator.addEventListener('touchstart', handleMouseDown, { passive: false });
  
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('touchmove', handleMouseMove, { passive: false });
  
  document.addEventListener('mouseup', handleMouseUp);
  document.addEventListener('touchend', handleMouseUp);
  
  container.addEventListener('scroll', updateIndicator);
  window.addEventListener('resize', () => {
    setTimeout(checkScrollable, 100);
  });
  
  // 初期化
  setTimeout(() => {
    checkScrollable();
    container.scrollLeft = 0;
  }, 50);
}

/* ────────────────────────────────────────────────────────────── */
/* ===== 3. 初期化システム ===== */
/* ────────────────────────────────────────────────────────────── */

// フル機能版の初期化
function initAPScrollIndicators() {
  const containers = document.querySelectorAll('[data-ap-scroll="container"]:not([data-items])');

  containers.forEach(container => {
    if (container.hasAttribute('data-ap-scroll-initialized')) return;
    
    const parent = container.parentElement;
    const indicator = parent.querySelector('[data-ap-scroll="indicator"]');
    
    if (indicator) {
      const scrollIndicator = new APScrollIndicator(container, indicator);
      container.setAttribute('data-ap-scroll-initialized', 'true');
      container._apScrollIndicator = scrollIndicator;
    }
  });
}

// データ属性対応版の初期化
function initDataScrollIndicators() {
  const dataContainers = document.querySelectorAll('[data-ap-scroll="container"][data-items]');
  
  dataContainers.forEach(container => {
    if (container.hasAttribute('data-scroll-initialized')) return;
    
    const parent = container.parentElement;
    const indicator = parent.querySelector('[data-ap-scroll="indicator"]');
    
    if (indicator) {
      setupDataScrollIndicator(container, indicator);
      container.setAttribute('data-scroll-initialized', 'true');
    }
  });
}

// 複数のタイミングで初期化を試行
document.addEventListener('DOMContentLoaded', () => {
  initAPScrollIndicators();
  initDataScrollIndicators();
  
  // 画像読み込み完了を待って再チェック
  setTimeout(() => {
    initAPScrollIndicators();
    initDataScrollIndicators();
  }, 500);
  setTimeout(() => {
    initAPScrollIndicators();
    initDataScrollIndicators();
  }, 1500);
});

// ページ読み込み完了後にも実行
window.addEventListener('load', () => {
  setTimeout(() => {
    initAPScrollIndicators();
    initDataScrollIndicators();
  }, 100);
});

// DOM変更監視（動的コンテンツ対応）
const apScrollObserver = new MutationObserver(() => {
  // debounce処理
  clearTimeout(apScrollObserver.timeout);
  apScrollObserver.timeout = setTimeout(() => {
    initAPScrollIndicators();
    initDataScrollIndicators();
  }, 100);
});
apScrollObserver.observe(document.body, {
  childList: true,
  subtree: true
});

// リサイズ時の処理
window.addEventListener('resize', () => {
  clearTimeout(window.apScrollResizeTimeout);
  window.apScrollResizeTimeout = setTimeout(() => {
    document.querySelectorAll('[data-ap-scroll="container"]').forEach(container => {
      if (container._apScrollIndicator) {
        container._apScrollIndicator.recheckIndicator();
      }
    });
  }, 100);
});

// タブ切り替え時の再初期化（ランキングタブ等）
document.addEventListener('click', (e) => {
  if (e.target.classList.contains('category-button')) {
    setTimeout(() => {
      initAPScrollIndicators();
      initDataScrollIndicators();
      
      setTimeout(() => {
        document.querySelectorAll('[data-ap-scroll="container"]:not([data-items])').forEach(container => {
          if (container._apScrollIndicator) {
            container._apScrollIndicator.recheckIndicator();
          }
        });
      }, 200);
    }, 100);
  }
});

/* ────────────────────────────────────────────────────────────── */
/* ===== 4. マウスドラッグによる横スクロール機能 ===== */
/* ────────────────────────────────────────────────────────────── */
/**
 * コンテナ内でのマウスドラッグによる横スクロール
 * data-ap-scroll="container"を持つ全要素に自動適用
 */

function enableMouseDragScroll(container) {
  let isDragging = false;
  let startX = 0;
  let startScrollLeft = 0;
  let dragged = false;
  const THRESHOLD = 5;

  // ドラッグ開始
  container.addEventListener('mousedown', (e) => {
    e.preventDefault(); // 画像ドラッグ選択を防止

    isDragging = true;
    startX = e.clientX;
    startScrollLeft = container.scrollLeft;
    dragged = false;

    container.classList.add('is-dragging');
    container.style.scrollSnapType = 'none';
  });

  // スクロール追従
  document.addEventListener('mousemove', (e) => {
    if (!isDragging) return;

    const deltaX = e.clientX - startX;
    if (Math.abs(deltaX) > THRESHOLD) dragged = true;

    container.scrollLeft = startScrollLeft - deltaX;
  });

  // ドラッグ終了
  const endDrag = () => {
    if (!isDragging) return;
    isDragging = false;

    container.classList.remove('is-dragging');
    container.style.scrollSnapType = '';
  };
  document.addEventListener('mouseup', endDrag);
  document.addEventListener('mouseleave', endDrag);

  // ドラッグ後の誤クリック無効化
  container.addEventListener('click', (e) => {
    if (dragged) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);
}

// 初期化
function initDrag() {
  document
    .querySelectorAll('[data-ap-scroll="container"]:not([data-drag-init])')
    .forEach((el) => {
      enableMouseDragScroll(el);
      el.setAttribute('data-drag-init', '1');
    });
}

document.addEventListener('DOMContentLoaded', initDrag);

// 動的コンテンツ対応
const mo = new MutationObserver(initDrag);
mo.observe(document.body, { childList: true, subtree: true });

/* ────────────────────────────────────────────────────────────── */
/* ===== 5. 商品詳細ページ用アコーディオン ===== */
/* ────────────────────────────────────────────────────────────── */
/**
 * シンプルなアコーディオン機能
 * 
 * HTML構造:
 * <div class="custom-accordion-item">
 *   <div class="custom-title">タイトル</div>
 *   <div class="custom-content">コンテンツ</div>
 * </div>
 */

document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('.custom-accordion-item').forEach(function(item) {
    var title   = item.querySelector('.custom-title');
    var content = item.querySelector('.custom-content');

    title.addEventListener('click', function() {
      // 開いているかどうかは style.maxHeight の有無で判定
      if (content.style.maxHeight) {
        // 閉じる
        content.style.maxHeight = null;
        title.classList.remove('active');
      } else {
        // 開く（実際の高さを指定）
        content.style.maxHeight = content.scrollHeight + 'px';
        title.classList.add('active');
      }
    });
  });
});

/* ────────────────────────────────────────────────────────────── */
/* ===== 6. SPF検索ページ用Wishlistアイコン位置調整 ===== */
/* ────────────────────────────────────────────────────────────── */
/**
 * Smart Product Filter (SPF) アプリで生成される検索結果ページで
 * wishlistアイコンを商品画像エリア内の左下に移動させる
 */

// function moveWishlistIconsInSPF() {
//   // SPF商品カード内のwishlistアイコンを探す
//   const spfCards = document.querySelectorAll('.spf-product-card');
  
//   spfCards.forEach(card => {
//     const wishlistIcon = card.querySelector('.wishlisthero-floating');
//     const imageWrapper = card.querySelector('.spf-product-card__image-wrapper');
    
//     if (wishlistIcon && imageWrapper && !wishlistIcon.hasAttribute('data-moved')) {
//       // アイコンを画像エリア内に移動
//       imageWrapper.appendChild(wishlistIcon);
//       wishlistIcon.setAttribute('data-moved', 'true');
      
//       // 画像エリアに相対ポジションを設定
//       imageWrapper.style.position = 'relative';
//     }
//   });
// }

// // 初期化関数
// function initSPFWishlistMove() {
//   moveWishlistIconsInSPF();
// }

// // DOM読み込み完了時に実行
// document.addEventListener('DOMContentLoaded', initSPFWishlistMove);

// // 動的コンテンツ読み込み対応（SPFアプリは検索時にAjaxでコンテンツを更新するため）
// const spfObserver = new MutationObserver(function(mutations) {
//   let shouldCheck = false;
  
//   mutations.forEach(function(mutation) {
//     if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
//       mutation.addedNodes.forEach(function(node) {
//         if (node.nodeType === 1) { // Element node
//           // SPF関連の要素が追加された場合
//           if (node.classList && (node.classList.contains('spf-product-card') || 
//               node.querySelector && node.querySelector('.spf-product-card'))) {
//             shouldCheck = true;
//           }
//         }
//       });
//     }
//   });
  
//   if (shouldCheck) {
//     // 少し遅延させてから実行（SPFの完全な読み込みを待つ）
//     setTimeout(moveWishlistIconsInSPF, 100);
//   }
// });

// // body全体を監視
// spfObserver.observe(document.body, {
//   childList: true,
//   subtree: true
// });

// // ページ読み込み完了後にも再実行
// window.addEventListener('load', function() {
//   setTimeout(initSPFWishlistMove, 500);
// });


// TOPページ　NEW ARRIVALS　SP時タブ改行
document.addEventListener("DOMContentLoaded", function () {

  function insertBrForSP() {
    if (window.innerWidth <= 959) {

      document.querySelectorAll('.featured-collections-navigation .h2').forEach(function(btn) {

        if (btn.textContent.trim() === "ateliers PENELOPE") {
          btn.innerHTML = "ateliers<br>PENELOPE";
        }

        if (btn.textContent.trim() === "BIQUI par ateliers PENELOPE") {
          btn.innerHTML = "BIQUI par ateliers<br>PENELOPE";
        }

      });

    }
  }

  insertBrForSP();

  window.addEventListener("resize", insertBrForSP);

});

// 外部URLの場別ウインドウ　2026.3.2 松本
var links = document.links;
 for (let i = 0, linksLength = links.length ; i < linksLength ; i++) {
  if (links[i].hostname !== window.location.hostname) {
   links[i].target = '_blank';
   links[i].rel = 'noreferrer noopener';
  }
 }